import { StoreType } from '@tock/shared/utils/connect';
import { core } from '@tock/coretypes';
import { createSelector } from 'reselect';
import { orderBy } from 'es-toolkit/compat';
import { useSelector } from 'react-redux';

export const getApp = (state: StoreType) => state.app;

export const getAppConfig = (state: StoreType) => state.app.config;

export const useApp = () => useSelector(getApp);

export const getMetroAreas = (state: StoreType) => state.app.config?.metroArea;

export const getInMobileNative = (state: StoreType) => state.app.inReactNative;

export const getBusinessConfigSelector = (state: StoreType) => state.app.config?.business;

export const appPatronSelector = createSelector(
  (state: StoreType) => state.app.patron,
  (patron): core.Patron | undefined => patron || undefined
);

export const getDetectedLocation = (state: StoreType) => state.app.detectedLocation;
export const getAppPatron = (state: StoreType) => state.app.patron;
export const getAppPatronProfile = (state: StoreType) => state.app.patronProfile;
export const getEnv = (state: StoreType) => state.app.settings.ENV;

/**
 * exported for testing
 */
export const allMetroAreas = (metroAreas: core.IMetroArea[] | undefined): core.IMetroArea[] => {
  const sortedMetroAreas = orderBy(metroAreas || [], 'name');
  return sortedMetroAreas;
};

export const allMetroAreasSelector = createSelector(getMetroAreas, allMetroAreas);

export const appPatronProfileSelector = createSelector(
  (state: StoreType) => state.app.patronProfile,
  (patronProfile): core.PatronProfile | undefined => patronProfile || undefined
);

export const appPatronLocationSelector = (state: StoreType) => state.app.patronLocation;

export const appRequestInProgressSelector = (state: StoreType) => state.app.requestInProgress;

export const getGoogleMapsState = (state: StoreType) => ({
  googleMapsLoaded: state.app.googleMapsLoaded,
  googleMapsLoadError: state.app.googleMapsLoadError,
});

export const getTockGAPropertiesSelector = (state: StoreType) => ({
  ga4Key: state.app.settings.GA4_KEY,
  uaKey: state.app.settings.GA_KEY,
  optimizeId: state.app.settings.GOOGLE_OPTIMIZE_CONTAINER_ID,
});

export const getTockAmplitudePropertiesSelector = (state: StoreType) => ({
  amplitudeKey: state.app.settings.AMPLITUDE_KEY,
});

export const getTockGleapPropertiesSelector = (state: StoreType) => ({
  gleapKey: state.app.settings.GLEAP_KEY,
});

export const getBusinessAnalyticsPropertiesSelector = (state: StoreType) => ({
  businessWebProperty: state.app.config?.business?.googleAnalyticsWebProperty,
  businessWebPropertyGA4: state.app.config?.business?.googleAnalyticsWebProperty_4,
  businessWebPropertyUa: state.app.config?.business?.googleAnalyticsWebPropertyUa,
  trackedDomains: state.app.config?.business?.googleAnalyticsTrackedDomains,
  webUrl: state.app.config?.business?.webUrl,
});

export const getFacebookPixelDetailsSelector = (state: StoreType) => ({
  facebookPixelId: state.app.settings.FACEBOOK_PIXEL_ID,
  businessFacebookPixelId: state.app.config?.business?.facebookPixelId,
  businessFacebookBusinessPixelId: state.app.config?.business?.facebookBusinessPixelId,
});

export const getTurnstileSiteKeySelector = (state: StoreType) =>
  state.app.settings.TURNSTILE_SITE_KEY;

export const getIsAuthenticated = (state: StoreType) => !!state.app.jwtToken;
export const getPatronAuthTypes = (state: StoreType) => state.app.patronAuthTypes;

export const getIsAwaitingPatronProfileInitialization = createSelector(
  (state: StoreType) => state.app.patron,
  getIsAuthenticated,
  (patron, getIsAuthenticated): boolean => getIsAuthenticated && !patron
);

// exported for testing
export const computeOwnerAbleToSetupAccount = (
  isAuthenticated: boolean,
  patronAuthTypes?: string[] | null
) => {
  return !isAuthenticated && Array.isArray(patronAuthTypes) && patronAuthTypes.length === 0;
};

export const ownerAbleToSetupAccountSelector = createSelector(
  getIsAuthenticated,
  getPatronAuthTypes,
  computeOwnerAbleToSetupAccount
);

export const explorePathSelector = (state: StoreType) => state.app.settings.EXPLORE_PATH;

export const getLock = (state: StoreType) => state.lock;

export const getReschedule = (state: StoreType) => state.reschedule;

export const sessionTokenSelector = (state: StoreType) => state.app.sessionToken;

export const businessIdSelector = (state: StoreType) => state.app.config?.business?.id;
